import { useNavigate } from 'react-router';

import { Copy, Edit, Eye, Trash2 } from 'lucide-react';

import { ADVANCED_CREATORS } from '@/constants';
import { useTemplateListContext } from '@/contexts/TemplatesContext';
import { useConfirmModal } from '@/hooks/useConfirmModal';
import { useNotificationModal } from '@/hooks/useNotificationModal';
import { apiService } from '@/lib/axios';

import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { TableBody, TableCell, TableRow } from '../ui/table';
import { TooltipButton } from '../ui/tooltip-button';

const TemplateListTableBody = () => {
  const navigate = useNavigate();

  const { currentPage, itemsPerPage, refetch, templates } =
    useTemplateListContext();

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTemplates = templates.slice(startIndex, endIndex);

  const isDisableEdition = (currentTemplate) =>
    currentTemplate && ADVANCED_CREATORS.includes(currentTemplate.creator);

  const { showConfirmModal } = useConfirmModal();
  const { showNotificationModal } = useNotificationModal();

  const handleDefaultToggle = async (templateId) => {
    try {
      const response = await apiService.templates.setDefault(templateId);

      if (response.data.success) {
        await refetch();

        showNotificationModal(
          'Default Set',
          response.data.message || 'Template set as default successfully',
          'success'
        );
      } else {
        throw new Error(response.data.error || 'Failed to set default');
      }
    } catch (err) {
      console.error('Error setting default:', err);

      showNotificationModal(
        'Error',
        err.response?.data?.error || 'Failed to set template as default',
        'error'
      );
    }
  };

  const handleEdit = async (templateId) => {
    navigate(`/template/${templateId}`);
  };

  const handlePreview = async (templateId) => {
    navigate(`/template/${templateId}/preview`);
  };

  const handleDuplicate = async (templateId) => {
    try {
      const response = await apiService.templates.getById(templateId);

      if (!response.data.success) return;

      const template = response.data.data;

      showConfirmModal({
        title: 'Duplicate Template',
        description: `Are you sure you want to create a copy of the template "${template.name}"?`,
        onConfirm: async () => {
          try {
            const duplicateResponse =
              await apiService.templates.duplicate(templateId);

            if (duplicateResponse.data.success) {
              const newTemplateId = duplicateResponse.data.data.id;

              navigate(`/template/${newTemplateId}`);
              showNotificationModal(
                'Template duplicated',
                `Template "${template.name}" has been successfully duplicated.`,
                'success'
              );
            } else {
              throw new Error(
                duplicateResponse.data.error || 'Failed to duplicate template'
              );
            }
          } catch (err) {
            console.error('Error duplicating template:', err);

            showNotificationModal(
              'Duplication Failed',
              err.response?.data?.error ||
                `Failed to duplicate template "${template.name}". Please try again.`,
              'error'
            );
          }
        },
      });
    } catch (err) {
      console.error('Error loading template for duplication:', err);

      showNotificationModal(
        'Error',
        'Failed to load template for duplication',
        'error'
      );
    }
  };

  const handleDelete = async (templateId) => {
    try {
      const response = await apiService.templates.getById(templateId);

      if (!response.data.success) return;

      const template = response.data.data;

      showConfirmModal({
        title: 'Delete Template',
        description: `Are you sure you want to delete the template "${template.name}"? This action cannot be undone.`,
        onConfirm: async () => {
          try {
            const deleteResponse =
              await apiService.templates.delete(templateId);

            if (deleteResponse.data.success) {
              await refetch();

              showNotificationModal(
                'Template Deleted',
                deleteResponse.data.message ||
                  `Template "${template.name}" has been successfully deleted.`,
                'success'
              );
            } else {
              throw new Error(
                deleteResponse.data.error || 'Failed to delete template'
              );
            }
          } catch (err) {
            console.error('Error deleting template:', err);

            showNotificationModal(
              'Delete Failed',
              err.response?.data?.error ||
                `Failed to delete template "${template.name}". Please try again.`,
              'error'
            );
          }
        },
        variant: 'destructive',
      });
    } catch (err) {
      console.error('Error loading template for deletion:', err);

      showNotificationModal(
        'Error',
        'Failed to load template for deletion',
        'error'
      );
    }
  };

  return (
    <TableBody>
      {paginatedTemplates.map((template) => (
        <TableRow key={template.id}>
          <TableCell>
            <Badge variant="outline">{template.type}</Badge>
          </TableCell>
          <TableCell className="font-medium">{template.name}</TableCell>
          <TableCell>{template.creator}</TableCell>
          <TableCell>
            <Switch
              checked={template.default}
              onCheckedChange={() => handleDefaultToggle(template.id)}
            />
          </TableCell>
          <TableCell>
            <div className="flex items-center space-x-2">
              <TooltipButton
                disabled={isDisableEdition(template) || template.isUsed}
                tooltip="Edit"
                variant="ghost"
                size="icon"
                onClick={() => handleEdit(template.id)}
              >
                <Edit className="h-4 w-4" />
              </TooltipButton>
              <TooltipButton
                tooltip="Preview"
                variant="ghost"
                size="icon"
                onClick={() => handlePreview(template.id)}
              >
                <Eye className="h-4 w-4" />
              </TooltipButton>
              <TooltipButton
                tooltip="Duplicate"
                variant="ghost"
                size="icon"
                onClick={() => handleDuplicate(template.id)}
              >
                <Copy className="h-4 w-4" />
              </TooltipButton>
              <TooltipButton
                tooltip="Delete"
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(template.id)}
                disabled={
                  isDisableEdition(template) ||
                  template.default ||
                  template.isUsed
                }
              >
                <Trash2 className="h-4 w-4" />
              </TooltipButton>
            </div>
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
};

export default TemplateListTableBody;
